import { useState, useEffect } from "react";
import { BiArrowBack, BiCheck } from "react-icons/bi";
import { Link, useSearchParams } from "react-router-dom";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../config/firebase";

const BookingSuccess = () => {
  const [searchParams] = useSearchParams();
  const bookingReference = searchParams.get("ref");
  const extrasAdded = searchParams.get("extrasAdded") === "true";
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [billingInfo, setBillingInfo] = useState(null);

  useEffect(() => {
    if (!bookingReference) return;
    const fetchBooking = async () => {
      try {
        let data = null;

        // Try by bookingReference field first
        const q = query(
          collection(db, "bookings"),
          where("bookingReference", "==", bookingReference),
        );
        const snap = await getDocs(q);

        if (!snap.empty) {
          data = snap.docs[0].data();
        } else {
          // Fallback: ref in URL might be the Firestore doc ID
          const directDoc = await getDoc(doc(db, "bookings", bookingReference));
          if (directDoc.exists()) {
            data = directDoc.data();
          }
        }

        if (!data) return;

        // Always populate billing info
        const ci = data.customerInfo || {};
        setBillingInfo({
          name: ci.name || ci.fullName || "",
          email: ci.email || "",
          phone: ci.phone || ci.contact || "",
          address: ci.address || "",
        });

        // BKT payment details (only when bank fields are present)
        if (data.bktAuthCode || data.bktMaskedCard || data.bktOrderId) {
          setPaymentDetails({
            orderId: data.bktOrderId || bookingReference,
            maskedCard: data.bktMaskedCard || "",
            authCode: data.bktAuthCode || "",
            transactionType: data.bktTransactionType || "Auth",
            totalPrice: data.totalPrice ?? null,
          });
        }
      } catch (e) {
        console.error("BookingSuccess fetch error:", e);
      }
    };
    fetchBooking();
  }, [bookingReference]);

  return (
    <div className="min-h-screen flex-center-center py-10">
      <div className="rounded-lg p-4 bg-white border dark:bg-card-dark dark:border-dark text-center max-w-[600px] w-[95%]">
        <div className="icon-box !w-16 !h-16 !bg-secondaryBlue mx-auto text-white">
          <BiCheck className="text-6xl" />
        </div>
        <h1 className="text-4xl font-bold mt-3">
          {extrasAdded
            ? "Extras Added Successfully!"
            : "Thank you!! You've successfully completed your booking"}
        </h1>

        {bookingReference && (
          <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Your Booking Reference
            </p>
            <p className="text-2xl sm:text-3xl font-bold text-blue-600 dark:text-blue-400 font-mono">
              {bookingReference}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Use this reference to manage your booking or add extras later
            </p>
          </div>
        )}

        <p className="mt-4">
          {extrasAdded
            ? "Your extras have been added to your booking. A confirmation email has been sent to your email address."
            : "Your booking has been successfully confirmed and payment processed. A confirmation email with your booking reference has been sent to your email address."}
        </p>

        {/* BKT Payment Details */}
        {paymentDetails && (
          <div className="mt-6 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden text-left">
            <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
              <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                Payment Details
              </p>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {paymentDetails.orderId && (
                <div className="flex justify-between items-center px-4 py-2.5">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Order ID
                  </span>
                  <span className="text-xs font-mono font-semibold text-gray-800 dark:text-gray-200">
                    {paymentDetails.orderId}
                  </span>
                </div>
              )}
              {paymentDetails.maskedCard && (
                <div className="flex justify-between items-center px-4 py-2.5">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Card
                  </span>
                  <span className="text-xs font-mono font-semibold text-gray-800 dark:text-gray-200">
                    {paymentDetails.maskedCard}
                  </span>
                </div>
              )}
              {paymentDetails.authCode && (
                <div className="flex justify-between items-center px-4 py-2.5">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Authorization Code
                  </span>
                  <span className="text-xs font-mono font-semibold text-gray-800 dark:text-gray-200">
                    {paymentDetails.authCode}
                  </span>
                </div>
              )}
              {paymentDetails.transactionType && (
                <div className="flex justify-between items-center px-4 py-2.5">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Transaction Type
                  </span>
                  <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                    {paymentDetails.transactionType}
                  </span>
                </div>
              )}
              {paymentDetails.totalPrice != null && (
                <div className="flex justify-between items-center px-4 py-2.5 bg-gray-50 dark:bg-gray-800/50">
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    Total Paid
                  </span>
                  <span className="text-sm font-bold text-green-600 dark:text-green-400">
                    €{parseFloat(paymentDetails.totalPrice).toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Billing Information */}
        {billingInfo && (billingInfo.name || billingInfo.email) && (
          <div className="mt-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden text-left">
            <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
              <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                Billing Information
              </p>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {billingInfo.name && (
                <div className="flex justify-between items-center px-4 py-2.5">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Name
                  </span>
                  <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                    {billingInfo.name}
                  </span>
                </div>
              )}
              {billingInfo.email && (
                <div className="flex justify-between items-center px-4 py-2.5">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Email
                  </span>
                  <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                    {billingInfo.email}
                  </span>
                </div>
              )}
              {billingInfo.phone && (
                <div className="flex justify-between items-center px-4 py-2.5">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Contact Number
                  </span>
                  <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                    {billingInfo.phone}
                  </span>
                </div>
              )}
              {billingInfo.address && (
                <div className="flex justify-between items-center px-4 py-2.5">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Address
                  </span>
                  <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                    {billingInfo.address}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Cancellation & Contact Notice */}
        <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800/50 dark:bg-amber-900/10 px-4 py-3 text-left space-y-2">
          <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
            <span className="font-semibold">Cancellation Policy:</span> No
            refund will be issued after 30 days from the booking date. Please
            review our{" "}
            <Link
              to="/privacy-policy"
              className="underline hover:text-amber-900 dark:hover:text-amber-200"
            >
              Privacy Policy
            </Link>{" "}
            and{" "}
            <Link
              to="/terms-and-conditions"
              className="underline hover:text-amber-900 dark:hover:text-amber-200"
            >
              Terms &amp; Conditions
            </Link>{" "}
            on our website for full details.
          </p>
          <p className="text-xs text-amber-800 dark:text-amber-300">
            <span className="font-semibold">Questions?</span> Please contact us
            at{" "}
            <a
              href="tel:+35567386 9970"
              className="font-semibold underline hover:text-amber-900 dark:hover:text-amber-200"
            >
              +355 67 386 9970
            </a>
          </p>
        </div>

        <Link
          to="/"
          className="!opacity-100 mt-6 flex-center-center gap-3 text-secondaryBlue hover:underline"
        >
          <BiArrowBack />
          <p>Go back home</p>
        </Link>
      </div>
    </div>
  );
};

export default BookingSuccess;
